import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import Pagination from '@/components/Pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Calendar, Search, RefreshCw, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';

const BACKEND_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
const API = `${BACKEND_URL}/api`;

const Categories = () => {
  usePageTitle('Categorías');
  const navigate = useNavigate();
  const { token, canManageUsers } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [newCategory, setNewCategory] = useState({
    name: '',
    year: '',
    description: ''
  });
  
  // Recategorization state
  const [showRecategorizeDialog, setShowRecategorizeDialog] = useState(false);
  const [recategorizePreview, setRecategorizePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executingRecategorize, setExecutingRecategorize] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/categories`, {
        ...newCategory,
        year: parseInt(newCategory.year)
      });
      toast.success('Categoría creada exitosamente');
      setShowCreateDialog(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(error.response?.data?.detail || 'Error al crear categoría');
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/categories/${editingCategory.id}`, {
        ...editingCategory,
        year: parseInt(editingCategory.year)
      });
      toast.success('Categoría actualizada exitosamente');
      setShowEditDialog(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Error al actualizar categoría');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (window.confirm(`¿Estás seguro de eliminar la categoría "${categoryName}"?`)) {
      try {
        await axios.delete(`${API}/categories/${categoryId}`);
        toast.success('Categoría eliminada exitosamente');
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
      }
    }
  };

  const resetForm = () => {
    setNewCategory({
      name: '',
      year: '',
      description: ''
    });
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/players?category=${categoryId}`);
  };

  // Recategorization functions
  const handlePreviewRecategorize = async () => {
    setLoadingPreview(true);
    setRecategorizePreview(null);
    try {
      const response = await axios.get(`${API}/categories/recategorize/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecategorizePreview(response.data);
      setShowRecategorizeDialog(true);
    } catch (error) {
      console.error('Error fetching recategorization preview:', error);
      toast.error(error.response?.data?.detail || 'Error al obtener vista previa de recategorización');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleExecuteRecategorize = async () => {
    setExecutingRecategorize(true);
    try {
      const response = await axios.post(`${API}/categories/recategorize/execute`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setShowRecategorizeDialog(false);
      setRecategorizePreview(null);
      // Refresh categories in case counts changed
      fetchCategories();
    } catch (error) {
      console.error('Error executing recategorization:', error);
      toast.error(error.response?.data?.detail || 'Error al ejecutar recategorización');
    } finally {
      setExecutingRecategorize(false);
    }
  };

  // Filter and pagination
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCategories = filteredCategories.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            <p className="mt-4 text-gray-600">Cargando categorías...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Categorías</h1>
            <p className="text-gray-600 mt-1">{filteredCategories.length} categoría(s) encontrada(s)</p>
          </div>
          <div className="flex gap-2">
            {canManageUsers && (
              <Button
                onClick={handlePreviewRecategorize}
                variant="outline"
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
                disabled={loadingPreview}
                data-testid="recategorize-button"
              >
                {loadingPreview ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Recategorizar
              </Button>
            )}
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-brand-600 hover:bg-brand-700"
              data-testid="create-category-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Categoría
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Buscar categoría..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-category-input"
              />
            </div>
          </CardContent>
        </Card>

        {categories.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No hay categorías registradas</p>
              <p className="text-sm text-gray-500 mt-1">Crea una categoría para comenzar</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Nombre</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Año Nacimiento</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Descripción</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedCategories.map(category => (
                      <tr 
                        key={category.id} 
                        className="hover:bg-brand-50 cursor-pointer transition-colors" 
                        data-testid={`category-row-${category.id}`}
                        onClick={() => handleCategoryClick(category.id)}
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-brand-600 hover:text-brand-700">{category.name}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{category.year || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-600">{category.description || '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-brand-600 hover:bg-brand-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingCategory(category);
                                setShowEditDialog(true);
                              }}
                              data-testid={`edit-category-${category.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCategory(category.id, category.name);
                              }}
                              data-testid={`delete-category-${category.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredCategories.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              itemName="categoría(s)"
            />
          </Card>
        )}
      </div>

      {/* Create Category Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Nueva Categoría</DialogTitle>
            <DialogDescription>
              Define una nueva categoría para organizar los jugadores
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCategory}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre de la Categoría *</Label>
                <Input
                  id="name"
                  placeholder="Ej: Sub-15, Sub-17, Primera"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="year">Año de Nacimiento</Label>
                <Input
                  id="year"
                  type="number"
                  placeholder="Ej: 2012 (dejar vacío para categorías como Primera)"
                  value={newCategory.year}
                  onChange={(e) => setNewCategory({...newCategory, year: e.target.value ? parseInt(e.target.value) : ''})}
                  data-testid="category-year-input"
                />
                <p className="text-xs text-gray-500">Usado para asignar automáticamente jugadores según su fecha de nacimiento</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Input
                  id="description"
                  placeholder="Descripción opcional"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-brand-600 hover:bg-brand-700"
                data-testid="submit-create-category"
              >
                Crear Categoría
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Categoría</DialogTitle>
            <DialogDescription>
              Modifica la información de la categoría
            </DialogDescription>
          </DialogHeader>
          {editingCategory && (
            <form onSubmit={handleUpdateCategory}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Nombre de la Categoría *</Label>
                  <Input
                    id="edit-name"
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-year">Año de Nacimiento</Label>
                  <Input
                    id="edit-year"
                    type="number"
                    placeholder="Dejar vacío para categorías como Primera"
                    value={editingCategory.year || ''}
                    onChange={(e) => setEditingCategory({...editingCategory, year: e.target.value ? parseInt(e.target.value) : null})}
                  />
                  <p className="text-xs text-gray-500">Usado para asignar automáticamente jugadores según su fecha de nacimiento</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Descripción</Label>
                  <Input
                    id="edit-description"
                    value={editingCategory.description || ''}
                    onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                  />
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingCategory(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  Guardar Cambios
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Recategorization Dialog */}
      <Dialog open={showRecategorizeDialog} onOpenChange={(open) => {
        if (!open) {
          setShowRecategorizeDialog(false);
          setRecategorizePreview(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-500" />
              Recategorización de Jugadores
            </DialogTitle>
            <DialogDescription>
              Revisa los jugadores que serían movidos a categorías más apropiadas según su edad actual
            </DialogDescription>
          </DialogHeader>
          
          {recategorizePreview && (
            <div className="space-y-4">
              {/* Summary Card */}
              <Card className={recategorizePreview.total_changes_needed > 0 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}>
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    {recategorizePreview.total_changes_needed > 0 ? (
                      <AlertTriangle className="w-8 h-8 text-amber-500" />
                    ) : (
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    )}
                    <div>
                      <p className="font-semibold text-lg">
                        {recategorizePreview.total_changes_needed > 0 
                          ? `${recategorizePreview.total_changes_needed} jugador(es) requieren recategorización`
                          : 'Todos los jugadores están en su categoría óptima'
                        }
                      </p>
                      <p className="text-sm text-gray-600">
                        {recategorizePreview.total_players_checked} jugadores activos revisados
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Changes List */}
              {recategorizePreview.changes && recategorizePreview.changes.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-medium text-gray-900">Cambios propuestos:</h3>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {recategorizePreview.changes.map((change, idx) => (
                      <Card key={idx} className="border-gray-200">
                        <CardContent className="py-3 px-4">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">{change.player_name}</span>
                              <span className="text-sm text-gray-500">
                                (Nac. {change.birth_year} - {change.player_age} años)
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                                {change.current_category_name}
                              </span>
                              <ArrowRight className="w-4 h-4 text-gray-400" />
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                {change.optimal_category_name}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowRecategorizeDialog(false);
                setRecategorizePreview(null);
              }}
            >
              Cancelar
            </Button>
            {recategorizePreview && recategorizePreview.total_changes_needed > 0 && (
              <Button
                onClick={handleExecuteRecategorize}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                disabled={executingRecategorize}
                data-testid="execute-recategorize-button"
              >
                {executingRecategorize ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Aplicando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Aplicar {recategorizePreview.total_changes_needed} cambio(s)
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Categories;